import React, { useCallback, useEffect, useState, useImperativeHandle } from "react";
import { Button, Form, Grid, Message } from "semantic-ui-react";
import { convertToDateString, operationOfNumberArray, setBackgroundColor } from "../../Helpers";
import { clearMixedValueBulkEdit, getBulkEditFieldValue, handleBulkEditFieldChange, handleBulkEditModalClose } from "../../helpers/GlobalHelper";
import { getIsWhiteLabeled } from "../../helpers/PermissionHelper";
import { GlobalConstants } from "../constants/GlobalConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";
import BCFormInput from "../ui/BCFormInput";
import BCModal from "../ui/BCModal";
import BCTable from "../ui/BCTable";
import BCValidationMessage from "../ui/BCValidationMessage";

interface IBPPropertyUnitsTableProps {
  isloan?: boolean | null, 
  unitRef: any;
  unitRowRef?: any,
  handleUnitValue?: any,
  properties: any;
  info: any;
  setInfo: any;
  checkedUnits: any;
  setCheckedUnits: any;
  isUnitBulkEditModalOpen: boolean;
  setIsUnitBulkEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  brokerColor: any;
  handleUnitFormChange: any;
  disabled: any;
  invalidProps: any;
}

interface IPropertyUnit {
  unitNumber: string | null,
  numberOfBedroom: number | null,
  numberOfBathroom: number | null,
  numberOfCarGarage: number | null,
  squareFootage: number | null,
  completedValue: number | null,
  afterRepairValue: number | null,
}

export const BPPropertyUnitsTable = ({ 
  isloan = false, 
  unitRef,
  unitRowRef,
  handleUnitValue,
  properties,
  info, 
  setInfo, 
  checkedUnits, 
  setCheckedUnits, 
  isUnitBulkEditModalOpen,
  setIsUnitBulkEditModalOpen,
  brokerColor, 
  handleUnitFormChange, 
  disabled, 
  invalidProps,
}: IBPPropertyUnitsTableProps) => {
  const units = isloan ? "loanPropertyUnitReconveyances" : "units";
  const afterRepairValue = isloan ? "estimatedSalesPrice" : "completedValue";
  const allInitialProperties = isloan ? info.loanProperties : properties;
  const allInitialUnits = allInitialProperties ? allInitialProperties.reduce((acc, property) => Array.isArray(property[units]) ? [...acc, ...property[units]] : acc, []) : null;
  const emptyUnitInformationBulkEdit = {
    unitNumber: null,
    numberOfBedroom: null,
    numberOfBathroom: null,
    numberOfCarGarage: null,
    squareFootage: null,
    completedValue: null,
    afterRepairValue: null,
  };

  const [formattedUnitList, setformattedUnitList] = useState([] as any[]);
  const [unitInformationBulkEdit, setUnitInformationBulkEdit] = useState(emptyUnitInformationBulkEdit);

  const addUnitRowRef = useCallback((el) => {
    if (el && !unitRowRef.current.includes(el)) {
      unitRowRef.current.push(el);
    }
  }, [info]);

  const handleNewUnitTableDate = () => {
    const unitTable = [] as any[];
    properties.forEach(property => {
      if (property[units] && property[units].length) {
        property[units].forEach(unit => {
          unitTable.push({
            "id": unit.id,
            "Address": property.address,
            "Unit No.": <React.Fragment>
              {unitRowRef && <div key={`unit-ref-${unit.id}`} ref={addUnitRowRef} />}
              <BCFormInput
                type={GlobalConstants.FormInputTypes.INPUT}
                inputKey={`unit-${unit.id}`}
                field="unitNumber"
                obj={unit}
                value={handleDisplayUnitValue(unit, "unitNumber")}
                handleFormChange={(value, field) => handleUnitFormChange(value, field, unit, GlobalConstants.InputTypes.STRING, property)}
                disabled={disabled}
              />
              <BCValidationMessage invalidProps={invalidProps} prop={`custom-unit-${unit.id}-unitNumber`} />
              <BCValidationMessage invalidProps={invalidProps} prop={`custom-unit-duplicated-${unit.id}`} propText="Duplicated unit number." />
              </React.Fragment>,
            "Finished Value (i.e. ARV)": <React.Fragment>
              <BCFormInput
                type={GlobalConstants.FormInputTypes.INPUT}
                inputKey={`unit-completedValue-${unit.id}`}
                placeholder="100000"
                field={afterRepairValue}
                fieldType="dollar"
                inputType="number"
                obj={unit}
                value={handleDisplayUnitValue(unit, afterRepairValue)}
                handleFormChange={(value, field) => handleUnitFormChange(value, field, unit, GlobalConstants.InputTypes.DOLLAR, property)}
                disabled={disabled}
              />
              <BCValidationMessage invalidProps={invalidProps} prop={`custom-unit-${unit.id}-${afterRepairValue}`} />
            </React.Fragment>,
            "No. of Bedrooms": <React.Fragment>
            <BCFormInput 
              inputKey={`unit-${unit.id}`}
              type={GlobalConstants.FormInputTypes.INPUT}
              placeholder="1"
              obj={unit}
              value={handleDisplayUnitValue(unit, "numberOfBedroom")}
              field="numberOfBedroom"
              inputType={GlobalConstants.InputTypes.INT}
              handleFormChange={(value, field) => handleUnitFormChange(value, field, unit, GlobalConstants.InputTypes.INT, property)}
              disabled={disabled}
            />
            <BCValidationMessage invalidProps={invalidProps} prop={`custom-numberOfBedroom-${unit.id}`} />
          </React.Fragment>,
            "No. of Bathrooms": <>
            <BCFormInput 
              type={GlobalConstants.FormInputTypes.INPUT}
              inputKey={`unit-${unit.id}`}
              placeholder="1"
              obj={unit}
              value={handleDisplayUnitValue(unit, "numberOfBathroom")}
              field="numberOfBathroom"
              inputType={GlobalConstants.InputTypes.FLOAT}
              handleFormChange={(value, field) => handleUnitFormChange(value, field, unit, GlobalConstants.InputTypes.FLOAT, property)}
              disabled={disabled}
            />
            <BCValidationMessage invalidProps={invalidProps} prop={`custom-numberOfBathroom-${unit.id}`} />
          </>,
            "No. of Garage Bays": <React.Fragment>
            <BCFormInput 
              type={GlobalConstants.FormInputTypes.INPUT}
              inputKey={`unit-${unit.id}`}
              placeholder="1"
              obj={unit}
              value={handleDisplayUnitValue(unit, "numberOfCarGarage")}
              field="numberOfCarGarage"
              inputType={GlobalConstants.InputTypes.INT}
              handleFormChange={(value, field) => handleUnitFormChange(value, field, unit, GlobalConstants.InputTypes.INT, property)}
              disabled={disabled}
            />
            <BCValidationMessage invalidProps={invalidProps} prop={`custom-numberOfCarGarage-${unit.id}`} />
          </React.Fragment>,
            "Square Footage": <React.Fragment>
            <BCFormInput 
              type={GlobalConstants.FormInputTypes.INPUT}
              inputKey={`unit-${unit.id}`}
              placeholder="2000"
              obj={unit}
              value={handleDisplayUnitValue(unit, "squareFootage")}
              field="squareFootage"
              inputType={GlobalConstants.InputTypes.INT}
              handleFormChange={(value, field) => handleUnitFormChange(value, field, unit, GlobalConstants.InputTypes.INT, property)}
              disabled={disabled}
            />
            <BCValidationMessage invalidProps={invalidProps} prop={`custom-squareFootage-${unit.id}`} />
          </React.Fragment>
          });
        });

        if (isloan) {
          unitTable.forEach(row => delete row["Address"]);
        }
      }
    });

    setformattedUnitList(unitTable);
  };

  const handleDisplayUnitValue = (unit, field) => {
    if (isloan) {
      return handleUnitValue ? handleUnitValue(info, unit, field) : null;
    }
    else if (unit && unit.field){
      return unit.field;
    }

    return null;
  };

  const handleUnitCheckbox = (unitId) => {
    if (checkedUnits.includes(unitId)) {
      checkedUnits = [...checkedUnits.filter(x => x !== unitId)];
    }
    else {
      checkedUnits = [...checkedUnits, unitId];
    }

    if (isloan && properties && properties[0]) {
      const unitIdsFromSameProperty = new Set();
      properties[0][units].map(unit => unitIdsFromSameProperty.add(unit.id));
      checkedUnits = [...checkedUnits].filter(unit => unitIdsFromSameProperty.has(unit));
    }

    setCheckedUnits(checkedUnits);
  };

  const handleCheckAllUnits = () => {
    let allUnitId = [] as string[];
    properties.forEach(property => {
      if (property[units]) {
        property[units].forEach(unit => {
          allUnitId = unit.id ? [...allUnitId, unit.id] : allUnitId;
        });
      }
    });

    if (JSON.stringify(allUnitId) === JSON.stringify(checkedUnits)) {
      setCheckedUnits([]);
    }
    else {
      setCheckedUnits(allUnitId);
    }
  };

  const getUnitBulkEditFieldValue = getBulkEditFieldValue.bind(
    null,
    checkedUnits,
    unitInformationBulkEdit,
    allInitialUnits
  );

  const handleUnitBulkEditFieldChange = handleBulkEditFieldChange.bind(null, setUnitInformationBulkEdit);

  const handleUnitBulkEditModalClose = handleBulkEditModalClose.bind(
    null,
    setIsUnitBulkEditModalOpen,
    setUnitInformationBulkEdit,
    emptyUnitInformationBulkEdit,
  );

  const handleApplyUnitInformationBulkEdit = (): void => {
    if (!isloan) {
      setInfo((prevObject) => ({
        ...prevObject,
      hasPropertiesChanged: true,
      properties: info.properties.map((prevProperty) => ({
        ...prevProperty,
        units: Array.isArray(prevProperty.units) ?
        prevProperty.units.map((unit) => {
          if (checkedUnits.indexOf(unit.id as string) !== -1) {
            for (const key in unitInformationBulkEdit) {
                if (unitInformationBulkEdit[key] && unitInformationBulkEdit[key] !== GlobalConstants.BulkEditValue.MIXED) {
                  unit[key] = unitInformationBulkEdit[key];
                }
              }
            }
            return unit;
          })
          : []
        }))
      }));
    }
    else {
      const keyOfUnit = (name: keyof IPropertyUnit) : string => name;
      info.loanProperties.forEach(property => {
        property[units]?.forEach(unit => {
          if (checkedUnits.find(x => x === unit.id)) {
            for (const key in unitInformationBulkEdit) {
              if (unitInformationBulkEdit[key] && unitInformationBulkEdit[key] !== GlobalConstants.BulkEditValue.MIXED) {
                if (key === keyOfUnit("unitNumber")) {
                  handleUnitFormChange(unitInformationBulkEdit[key], key, unit, GlobalConstants.InputTypes.STRING, property);
                }
                else if(key === keyOfUnit("afterRepairValue")){
                  handleUnitFormChange(unitInformationBulkEdit[key], key, unit, GlobalConstants.InputTypes.DOLLAR, property);
                }
                else {
                  handleUnitFormChange(unitInformationBulkEdit[key], key, unit, GlobalConstants.InputTypes.INT, property);
                }
              }
            }
          }
        });
      });
    }

    handleUnitBulkEditModalClose();
    setCheckedUnits([]);
  };

  useImperativeHandle(unitRef, () => ({
    applyUnitInformationBulkEdit() {
      handleApplyUnitInformationBulkEdit();
    }
  }));

  useEffect(() => {
    handleNewUnitTableDate();
  }, [handleUnitFormChange, info]);

  return (
    <React.Fragment>
      {(FeatureFlags.IS_UNIT_PROPERTY_REDESIGN) && <React.Fragment>
        {isUnitBulkEditModalOpen && <BCModal
          size="large"
          isModalOpen={isUnitBulkEditModalOpen}
          onOpen={() => setIsUnitBulkEditModalOpen(true)}
          header={
            <>
              <div>Edit {checkedUnits.length} Unit{checkedUnits.length > 1 && "s"}</div>
              <p className="gmr-modal-heading-description">Select all inputs you would like to apply edits to. The fields you do not change, will not change in the table.</p>
            </>
          }
          content={
            <Form className="primary-form" onSubmit={(e) => e.preventDefault()}>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="Unit No."
                      type={GlobalConstants.FormInputTypes.INPUT}
                      inputKey="unit-bulk-edit"
                      field="unitNumber"
                      value={getUnitBulkEditFieldValue("unitNumber")}
                      handleFormChange={handleUnitBulkEditFieldChange}
                    />
                  </Grid.Column>
                  {
                    ([
                      GlobalConstants.LoanProgramIds.ALL_IN_ONE,
                      GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION,
                      GlobalConstants.LoanProgramIds.FIX_AND_FLIP,
                      GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD
                    ].indexOf(info.loanProgramId) !== -1) && (
                      <Grid.Column width={16}>
                        <BCFormInput
                          label="Finished Value (i.e. ARV)"
                          type={GlobalConstants.FormInputTypes.INPUT}
                          inputKey="unit-completedValue-edit-bulk"
                          inputType={GlobalConstants.InputTypes.DOLLAR}
                          placeholder="100000"
                          field={afterRepairValue}
                          fieldType={!isNaN(+getUnitBulkEditFieldValue(afterRepairValue)) ? GlobalConstants.InputTypes.DOLLAR : GlobalConstants.InputTypes.STRING}
                          onFocus={() => clearMixedValueBulkEdit(setUnitInformationBulkEdit, getUnitBulkEditFieldValue(afterRepairValue), afterRepairValue)}
                          value={getUnitBulkEditFieldValue(afterRepairValue)}
                          handleFormChange={(value, field) => handleUnitBulkEditFieldChange(value, field, GlobalConstants.InputTypes.DOLLAR)}
                        />
                      </Grid.Column>
                    )
                  }
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="No. of Bedrooms"
                      inputKey="unit-bulk-edit"
                      type={GlobalConstants.FormInputTypes.INPUT}
                      placeholder="1"
                      field="numberOfBedroom"
                      inputType={GlobalConstants.InputTypes.INT}
                      handleFormChange={(value, field) => handleUnitBulkEditFieldChange(value, field, GlobalConstants.InputTypes.INT)}
                      value={getUnitBulkEditFieldValue("numberOfBedroom")}
                    />
                  </Grid.Column>
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="No. of Bathrooms"
                      type={GlobalConstants.FormInputTypes.INPUT}
                      inputKey="unit-bulk-edit"
                      placeholder="1"
                      field="numberOfBathroom"
                      value={getUnitBulkEditFieldValue("numberOfBathroom")}
                      handleFormChange={(value, field) => handleUnitBulkEditFieldChange(value, field, GlobalConstants.InputTypes.FLOAT)}
                      inputType={GlobalConstants.InputTypes.FLOAT}
                    />
                  </Grid.Column>
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="No. of Garage Bays"
                      type={GlobalConstants.FormInputTypes.INPUT}
                      inputKey="unit-bulk-edit"
                      placeholder="1"
                      field="numberOfCarGarage"
                      value={getUnitBulkEditFieldValue("numberOfCarGarage")}
                      handleFormChange={(value, field) => handleUnitBulkEditFieldChange(value, field, GlobalConstants.InputTypes.INT)}
                      inputType={GlobalConstants.InputTypes.INT}
                    />
                  </Grid.Column>
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="Square Footage"
                      type={GlobalConstants.FormInputTypes.INPUT}
                      inputKey="unit-edit-bulk"
                      placeholder="2000"
                      field="squareFootage"
                      value={getUnitBulkEditFieldValue("squareFootage")}
                      handleFormChange={handleUnitBulkEditFieldChange}
                      inputType={GlobalConstants.InputTypes.INT}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          }
          actions={
            <Grid>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <Button icon fluid onClick={handleUnitBulkEditModalClose}>
                    <div className="gmr-button-container">
                      <div className="gmr-button-text">Cancel</div>
                    </div>
                  </Button>
                </Grid.Column>
                <Grid.Column>
                  <Button 
                    className="bp-button" 
                    style={getIsWhiteLabeled() ? setBackgroundColor(brokerColor ? brokerColor : "#000000") : void 0}
                    fluid 
                    onClick={handleApplyUnitInformationBulkEdit}
                    >
                    <div className="gmr-button-container">
                      <div className="gmr-button-text">Apply Changes</div>
                    </div>
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          }
        />}
        <BCTable
          data={formattedUnitList}
          dataRules={[{ field: "id", rule: "hide" }]}
          handleCheckbox={(unitId) => handleUnitCheckbox(unitId)}
          handleCheckboxAll={() => handleCheckAllUnits()}
          checkedItems={checkedUnits}
          checkbox
          thin
          dataSingleLine
          unstackable
          brokerColor={brokerColor}
          overflowX
          requiredFields={["Unit No.", "Finished Value (i.e. ARV)"]}
        />
      </React.Fragment>}
    </React.Fragment>
  );
};

interface IBPConstructionScheduleTableProps {
  isLoan?: boolean | null, 
  setGreatestTotalToComplete?: any;
  setInfo?: React.Dispatch<any>;
  propertiesPatchArray?: any[];
  properties: any;
  checkedPropertySchedules: string[];
  setCheckedPropertySchedules: React.Dispatch<React.SetStateAction<string[]>>;
  projectInfo: any;
  handlePropertyLoanTermChange: any;
  brokerColor: any;
  disabled: boolean;
  invalidProps: string[];
}

export const BPConstructionScheduleTable = ({
  isLoan = false,
  setGreatestTotalToComplete,
  setInfo,
  propertiesPatchArray,
  properties, 
  checkedPropertySchedules, 
  setCheckedPropertySchedules, 
  projectInfo,
  handlePropertyLoanTermChange,
  brokerColor, 
  disabled, 
  invalidProps
} : IBPConstructionScheduleTableProps) => {
  const emptyLoanTermInformationBulkEdit = {
    monthOfFirstDraw: null,
    buildTimeInMonths: null,
    timeFrameToSellOrRefinanceInMonths: null,
  };

  const [formattedLoanTermTable, setFormattedLoanTermTable] = useState([] as any[]);
  const [warningMessage, setWarningMessage] = useState("" as string);
  const [totalRequestedLoanTerm, setTotalRequestedLoanTerm] = useState(0 as number);
  const [isLoanTermBulkEditModalOpen, setIsLoanTermBulkEditModalOpen] = useState(false as boolean);
  const [loanTermInformationBulkEdit, setLoanTermInformationBulkEdit] = useState(emptyLoanTermInformationBulkEdit);
  
  const handleWarningMessage = (loanProgram, loanTermMax, loanTerm) => {
    setWarningMessage(`The requested loan term of ${loanTerm} months exceeds the ${loanTermMax} month loan term maximum for the ${loanProgram} loan product. Please work with your loan team to shorten the requested loan term.`);
  };

  const handleCheckLoanTermLength = (property) => {
    const loanTerm = operationOfNumberArray("sum", [projectInfo.developmentBeginsAfterClosingInMonths, projectInfo.timeFrameToDevelopInMonths, property.monthOfFirstDraw, property.buildTimeInMonths, property.timeFrameToSellOrRefinanceInMonths, -1]);
    if (projectInfo.loanProgramId && loanTerm) {
      if (projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.ALL_IN_ONE && loanTerm > 24) {
        handleWarningMessage("All in One", 24, loanTerm);
      }
      else if (projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD && loanTerm > 24) {
        handleWarningMessage("Bridge to build", 24, loanTerm);
      }
      else if (projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION && loanTerm > 18) {
        handleWarningMessage("New Construction", 18, loanTerm);
      }
      else if (projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.FIX_AND_FLIP && loanTerm > 18) {
        handleWarningMessage("Flix and flip", 18, loanTerm);
      }
      else {
        setWarningMessage("");
      }
    }
  };

  const handleLoanTermCheckbox = (propertyId) => {
    if (checkedPropertySchedules.includes(propertyId)) {
      checkedPropertySchedules = [...checkedPropertySchedules.filter(x => x !== propertyId)];
    }
    else {
      checkedPropertySchedules = [...checkedPropertySchedules, propertyId];
    }

    setCheckedPropertySchedules(checkedPropertySchedules);
  };

  const handleCheckAllLoanTerms = () => {
    const allPropertyIds = properties.filter(property => !property.collateralProperty).map(property => property.id);
    if (JSON.stringify(allPropertyIds) === JSON.stringify(checkedPropertySchedules)) {
      setCheckedPropertySchedules([]);
    }
    else {
      setCheckedPropertySchedules(allPropertyIds);
    }
  };

  const handleLoanTermDisplayValue = (property, field) => {
    const selectedProperty = propertiesPatchArray?.find(x => x.id === property.id);
    return (selectedProperty && selectedProperty[field]) ? selectedProperty[field] : (property && property[field]) ? property[field] : null;
  };

  const getLoanTermBulkEditFieldValue = getBulkEditFieldValue.bind(
    null,
    checkedPropertySchedules,
    loanTermInformationBulkEdit,
    properties.filter(property => !property.collateralProperty)
  );

  const handleLoanTermBulkEditFieldChange = handleBulkEditFieldChange.bind(null, setLoanTermInformationBulkEdit);

  const handleLoanTermBulkEditModalClose = handleBulkEditModalClose.bind(
    null,
    setIsLoanTermBulkEditModalOpen,
    setLoanTermInformationBulkEdit,
    emptyLoanTermInformationBulkEdit
  );

  const handleApplyLoanTermInformationBulkEdit = () => {
    if (!isLoan && setInfo) {
      setInfo((prevObject) => ({
        ...prevObject,
        properties: prevObject.properties.map((property) => {
          if (checkedPropertySchedules.indexOf(property.id) !== -1) {
            for (const key in loanTermInformationBulkEdit) {
              if (loanTermInformationBulkEdit[key] && loanTermInformationBulkEdit[key] !== GlobalConstants.BulkEditValue.MIXED) {
                property[key] = loanTermInformationBulkEdit[key];
              }
            }
          }

          return property;
        })
      }));
    }
    else {
      properties.forEach(property => {
        if (checkedPropertySchedules.find(x => x === property.id)) {
          for (const key in loanTermInformationBulkEdit) {
            if (loanTermInformationBulkEdit[key] && loanTermInformationBulkEdit[key] !== GlobalConstants.BulkEditValue.MIXED) {
              handlePropertyLoanTermChange(loanTermInformationBulkEdit[key], key, property, GlobalConstants.InputTypes.INT);
            }
          }
        }
      });
    }

    handleLoanTermBulkEditModalClose();
    setCheckedPropertySchedules([]);
  };

  useEffect(() => {
    const handleLoanTermTableData = () => {
      const loanTermTable = [] as any;
      let payoffDate;
      if (properties) {
        properties.filter(property => !property.collateralProperty).forEach((property) => {
          if (isLoan) {
            const selectedProperty = propertiesPatchArray?.find(x => x.id === property.id);
            if (selectedProperty) {
              property = { ...property, ...selectedProperty };
            }
          }

          const completionDate = isLoan ? new Date(projectInfo.requestedClosingDate) : new Date(projectInfo.requestedClosingDate + "T00:00:00");
          if (property.monthOfFirstDraw) {
            const buildTimeInMonths = property.buildTimeInMonths ? property.buildTimeInMonths : 0;
            if (projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.ALL_IN_ONE) {
              completionDate.setMonth(completionDate.getMonth() + property.monthOfFirstDraw + buildTimeInMonths + projectInfo.developmentBeginsAfterClosingInMonths + projectInfo.timeFrameToDevelopInMonths);
            }
            else {
              completionDate.setMonth(completionDate.getMonth() + property.monthOfFirstDraw + buildTimeInMonths);
            }
  
            if (property.timeFrameToSellOrRefinanceInMonths) {
              payoffDate = new Date(completionDate);
              payoffDate.setMonth(payoffDate.getMonth() + property.timeFrameToSellOrRefinanceInMonths);
            }
          }

          loanTermTable.push({
            "id": property.id,
            "Address": isLoan ? property.street : property.address,
            "Month of First Draw (Start Month)": <React.Fragment>
              <BCFormInput
                type={GlobalConstants.FormInputTypes.INPUT}
                inputKey={`loan-schedule-${property.id}`}
                placeholder="1"
                obj={property}
                field="monthOfFirstDraw"
                inputType={GlobalConstants.InputTypes.INT}
                value={handleLoanTermDisplayValue(property , "monthOfFirstDraw")}
                handleFormChange={(value, field) => {handlePropertyLoanTermChange(value, field, property, GlobalConstants.InputTypes.INT); handleCheckLoanTermLength(property);}}
                disabled={disabled}
              />
              <BCValidationMessage invalidProps={invalidProps} prop={`custom-properties-monthOfFirstDraw-${property.id}`} />
            </React.Fragment>,
            "Months to Finish Construction": <React.Fragment>
              <BCFormInput
                type={GlobalConstants.FormInputTypes.INPUT}
                inputKey={`loan-schedule-${property.id}`}
                placeholder="1"
                obj={property}
                field="buildTimeInMonths"
                inputType={GlobalConstants.InputTypes.INT}
                value={handleLoanTermDisplayValue(property , "buildTimeInMonths")}
                handleFormChange={(value, field) => {handlePropertyLoanTermChange(value, field, property, GlobalConstants.InputTypes.INT); handleCheckLoanTermLength(property);}}
                disabled={disabled}
              />
              <BCValidationMessage invalidProps={invalidProps} prop={`custom-properties-buildTimeInMonths-${property.id}`} />
            </React.Fragment>,
            "Construction Completion Date": (property.buildTimeInMonths && completionDate) ? convertToDateString(completionDate, "date") : "",
            "Months to Sell Or Refinance": <React.Fragment>
              <BCFormInput
                type={GlobalConstants.FormInputTypes.INPUT}
                inputKey={`loan-schedule-${property.id}`}
                placeholder="1"
                obj={property}
                field="timeFrameToSellOrRefinanceInMonths"
                inputType={GlobalConstants.InputTypes.INT}
                value={handleLoanTermDisplayValue(property , "timeFrameToSellOrRefinanceInMonths")}
                handleFormChange={(value, field) => {handlePropertyLoanTermChange(value, field, property, GlobalConstants.InputTypes.INT); handleCheckLoanTermLength(property);}}
                disabled={disabled}
              />
              <BCValidationMessage invalidProps={invalidProps} prop={`custom-properties-timeFrameToSellOrRefinanceInMonths-${property.id}`} />
            </React.Fragment>,
            "Anticipated Payoff Date": (payoffDate && property.timeFrameToSellOrRefinanceInMonths) ? convertToDateString(payoffDate, "date") : "",
          });
        });
      }

      setFormattedLoanTermTable(loanTermTable);
    };

    const handleCalculateTotalLoanTerm = () => {
      let greatestLoanTermSum = 0;
      properties.forEach(property => {
        const loanTermSum = operationOfNumberArray("sum", [property.monthOfFirstDraw, property.buildTimeInMonths, property.timeFrameToSellOrRefinanceInMonths, -1]);
          if (loanTermSum && loanTermSum > greatestLoanTermSum) {
            greatestLoanTermSum = loanTermSum;
          }
      });

      setTotalRequestedLoanTerm(greatestLoanTermSum);
      if (setGreatestTotalToComplete) {
        setGreatestTotalToComplete(greatestLoanTermSum);
      }
    };

    handleLoanTermTableData();
    handleCalculateTotalLoanTerm();
  }, [handlePropertyLoanTermChange, warningMessage, totalRequestedLoanTerm]);

  return (
    <React.Fragment>
      {(FeatureFlags.IS_UNIT_PROPERTY_REDESIGN && (projectInfo.loanProgramId !== GlobalConstants.LoanProgramIds.BRIDGE && projectInfo.loanProgramId !== GlobalConstants.LoanProgramIds.DEVELOPMENT)) && <React.Fragment>
        {!disabled && <Button
          icon
          inverted
          color="green"
          className="table-button bottom-margin"
          onClick={() => setIsLoanTermBulkEditModalOpen(true)}
          disabled={checkedPropertySchedules.length < 1}
        >
          <div className="gmr-button-container">Edit</div>
        </Button>}
        <BCModal
          size="large"
          isModalOpen={isLoanTermBulkEditModalOpen}
          onOpen={() => setIsLoanTermBulkEditModalOpen(true)}
          header={
            <> 
              <div>Edit {checkedPropertySchedules.length} Row{checkedPropertySchedules.length > 1 && "s"}</div>
              <p className="gmr-modal-heading-description">Select all inputs you would like to make edit. The fields you do not change, will not change in the table.</p>
            </>
          }
          content={
            <Form className="primary-form" onSubmit={(e) => e.preventDefault()}>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="Month of First Draw (Start Month)"
                      type={GlobalConstants.FormInputTypes.INPUT}
                      inputKey="unit-bulk-edit"
                      placeholder="1"
                      field="monthOfFirstDraw"
                      value={getLoanTermBulkEditFieldValue("monthOfFirstDraw")}
                      handleFormChange={(value, field) => handleLoanTermBulkEditFieldChange(value, field, GlobalConstants.InputTypes.INT)}
                      inputType={GlobalConstants.InputTypes.INT}
                    />
                  </Grid.Column>
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="Months to Finish Construction"
                      type={GlobalConstants.FormInputTypes.INPUT}
                      inputKey="unit-edit-bulk"
                      placeholder="2000"
                      field="buildTimeInMonths"
                      value={getLoanTermBulkEditFieldValue("buildTimeInMonths")}
                      handleFormChange={(value, field) => handleLoanTermBulkEditFieldChange(value, field, GlobalConstants.InputTypes.INT)}
                      inputType={GlobalConstants.InputTypes.INT}
                    />
                  </Grid.Column>
                  <Grid.Column width={16}>
                    <BCFormInput
                      label="Months to Sell Or Refinance"
                      type={GlobalConstants.FormInputTypes.INPUT}
                      inputKey="unit-edit-bulk"
                      placeholder="2000"
                      field="timeFrameToSellOrRefinanceInMonths"
                      value={getLoanTermBulkEditFieldValue("timeFrameToSellOrRefinanceInMonths")}
                      handleFormChange={(value, field) => handleLoanTermBulkEditFieldChange(value, field, GlobalConstants.InputTypes.INT)}
                      inputType={GlobalConstants.InputTypes.INT}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          }
          actions={
            <Grid>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <Button icon fluid onClick={handleLoanTermBulkEditModalClose}>
                    <div className="gmr-button-container">
                      <div className="gmr-button-text">Cancel</div>
                    </div>
                  </Button>
                </Grid.Column>
                <Grid.Column>
                  <Button 
                    className="bp-button" 
                    style={getIsWhiteLabeled() ? setBackgroundColor(brokerColor ? brokerColor : "#000000") : void 0}
                    fluid 
                    onClick={handleApplyLoanTermInformationBulkEdit}
                    >
                    <div className="gmr-button-container">
                      <div className="gmr-button-text">Apply Changes</div>
                    </div>
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          }
        />
        <BCTable
          data={formattedLoanTermTable}
          dataRules={[{ field: "id", rule: "hide" }]}
          handleCheckbox={(propertyId) => handleLoanTermCheckbox(propertyId)}
          handleCheckboxAll={() => handleCheckAllLoanTerms()}
          checkedItems={checkedPropertySchedules}
          checkbox
          thin
          dataSingleLine
          unstackable
          brokerColor={brokerColor}
          overflowX
          requiredFields={["Month of First Draw (Start Month)", "Months to Finish Construction", "Months to Sell Or Refinance"]}
          lastTableRow={isLoan ? null : <div className="loan-term-table-sum">
            <div>Total Requested Loan Term</div>
            <div className="total-requested-loan-term-value">{totalRequestedLoanTerm > 0 ? `${totalRequestedLoanTerm} month${totalRequestedLoanTerm !== 0 ? "s" : ""}` : "0 months"}</div>
          </div>}
        />
        {(warningMessage) && <Message
          className="gmr-message" 
          negative color="yellow"
          onDismiss={() => setWarningMessage("")}
        >
          {warningMessage}
        </Message>}
      </React.Fragment>}
    </React.Fragment>
  );
};